import React, { useContext, useEffect, useState } from 'react';
import { Box, Grid, Typography, IconButton, useMediaQuery, useTheme, Container } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import StoreContext from '../../../../react-core-lib/components/app/store-context';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { useSnackbar } from '../../SnackbarProvider';
import { FeedbackCTA } from '../../feedback-cta';
import GradientBanner from '../../../UIControls/GradientBanner';
import { recoverWhiteboard } from '../../../helper/whiteboardUtils';
import WhiteboardCard from './WhiteboardCard';

const ArchivedWhiteboards = () => {
    const theme = useTheme();
    const { whiteboardStore, authStore, userDataStore } = useContext(StoreContext);
    const [archivedWhiteboards, setArchivedWhiteboards] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [page, setPage] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');

    const [imageMap, setImageMap] = useState({});
    const [sortDirection, setSortDirection] = useState('desc');
    const [hasSortedRecently, setHasSortedRecently] = useState(false);

    const whiteboardsPerPage = 6;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
    const showSnackbar = useSnackbar();
    const navigate = useNavigate();

    const paginatedWhiteboards = filtered.slice(page * whiteboardsPerPage, (page + 1) * whiteboardsPerPage);

    useEffect(() => {
        if (!whiteboardStore.isInitialized) return;

        const data = Object.values(whiteboardStore.whiteboardData).filter((wb) => wb.whiteboardStatus === 'archived');

        setArchivedWhiteboards(data);
        setFiltered(data);
    }, [whiteboardStore.whiteboardData, whiteboardStore.isInitialized]);

    const handleNextPage = () => {
        if ((page + 1) * whiteboardsPerPage < filtered.length) {
            setPage(page + 1);
        }
    };

    const handlePrevPage = () => {
        if (page > 0) {
            setPage(page - 1);
        }
    };
    const handleOpenMenu = (event) => {
        const newDirection = sortDirection === 'desc' ? 'asc' : 'desc';
        setSortDirection(newDirection);
        setHasSortedRecently(true);

        const sorted = [...archivedWhiteboards].sort((a, b) =>
            newDirection === 'asc' ? a.lastUpdated - b.lastUpdated : b.lastUpdated - a.lastUpdated,
        );
        setFiltered(sorted);
        setPage(0);
    };

    useEffect(() => {
        const fetchImages = async () => {
            const images = {};
            for (const wb of filtered) {
                if (wb.canvasImageLink) {
                    const image = await whiteboardStore.imageStore.getImageFromIndexedDB(wb.canvasImageLink);
                    if (image?.data) images[wb.canvasImageLink] = image.data;
                }
            }
            setImageMap(images);
        };
        fetchImages();
    }, [filtered]);

    const handleSearchInputChange = (e) => {
        const term = e.target.value.toLowerCase();
        setSearchTerm(term);
        setPage(0); // reset to first page on new search

        const result = archivedWhiteboards.filter((wb) => {
            return (
                wb.handoutContentName?.toLowerCase().includes(term) ||
                wb.handoutAlphabeticalName?.toLowerCase().includes(term) ||
                wb.handoutDocumentTitle?.toLowerCase().includes(term) ||
                wb.notes?.toLowerCase().includes(term) ||
                wb.fileTitle?.toLowerCase().includes(term) ||
                wb.patientName?.toLowerCase().includes(term)
            );
        });

        setFiltered(result);
    };

    const handleRecover = (wb) =>
        recoverWhiteboard(
            wb,
            userDataStore,
            whiteboardStore,
            authStore,
            setArchivedWhiteboards,
            setFiltered,
            showSnackbar,
        );

    return (
        <>
            <GradientBanner
                title="Archived Whiteboards"
                subtitle="Review and recover your whiteboards"
                placeholder="Search archived whiteboards..."
                searchTerm={searchTerm}
                onSearchChange={handleSearchInputChange}
                isMobile={isMobile}
                isTablet={isTablet}
                height={isMobile ? 'initial' : '450px'}
                paddingY={isMobile ? '48px' : '40px'}
                paddingBottom="40px"
                className="search-banner"
            />
            <Container
                component={'section'}
                maxWidth={'xl'}
                sx={{ backgroundColor: '#F0F0FC', px: isMobileOrTablet ? 2 : 6, pt: 3 }}
            >
                <Box
                    sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 2, pr: 2, mb: '67px' }}
                >
                    {/* Sort By Text */}
                    <Typography
                        onClick={handleOpenMenu}
                        sx={{
                            fontFamily: 'Inter',
                            fontWeight: 600,
                            fontSize: '14px',
                            lineHeight: '14px',
                            letterSpacing: '2px',
                            textTransform: 'uppercase',
                            color: '#1919194D',
                            cursor: 'pointer',
                        }}
                    >
                        SORT BY{' '}
                        <Typography component="span" sx={{ color: '#000453' }}>
                            RECENT
                        </Typography>
                        <IconButton size="small" sx={{ ml: 1, p: 0 }}>
                            {sortDirection === 'desc' ? (
                                <ExpandMoreIcon sx={{ color: '#000453' }} />
                            ) : (
                                <ExpandLessIcon sx={{ color: '#000453' }} />
                            )}
                        </IconButton>
                    </Typography>
                </Box>

                {filtered.length === 0 ? (
                    <Typography sx={{ textAlign: 'center', color: '#000453' }}>
                        No archived whiteboards found.
                    </Typography>
                ) : (
                    <Grid
                        container
                        spacing={3}
                        px={isMobile ? '48px' : '66px'}
                        sx={{ position: 'relative', ml: '0px!important', width: '100%' }}
                    >
                        {paginatedWhiteboards.map((whiteboard, index) => (
                            <WhiteboardCard
                                key={whiteboard.whiteboardId || index}
                                whiteboard={whiteboard}
                                isMobile={isMobile}
                                userDataStore={userDataStore}
                                authStore={authStore}
                                whiteboardStore={whiteboardStore}
                                showSnackbar={showSnackbar}
                                imageMap={imageMap}
                                index={index}
                                allowAddNote={false}
                                actionLabel="Recover"
                                confirmTitle="Are you sure you want to recover this whiteboard?"
                                confirmBody=""
                                handleToggleFavourite={() => {}}
                                handleEditWhiteboard={() => {
                                    sessionStorage.setItem(
                                        'lastWhiteboardState',
                                        JSON.stringify({
                                            whiteboardId: whiteboard.whiteboardId,
                                            handoutId: whiteboard.handoutId,
                                            canvasImageLink: whiteboard.canvasImageLink,
                                            canvasJsonLink: whiteboard.canvasJsonLink,
                                            fileName: whiteboard.fileName || '',
                                            eTag: whiteboard.eTag,
                                        }),
                                    );
                                    navigate(`/handout/${whiteboard.handoutId}`);
                                }}
                                handleArchiveWhiteboard={() => handleRecover(whiteboard)}
                                hideEdit={true}
                                hideFavourite={true}
                            />
                        ))}
                        {/* Pagination Controls */}
                        {filtered.length > 6 && (
                            <>
                                <IconButton
                                    onClick={handlePrevPage}
                                    disabled={page === 0}
                                    sx={{
                                        position: 'absolute',
                                        left: '-10px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        zIndex: 10,
                                        backgroundColor: 'rgba(0, 4, 83, 0.08)',
                                        width: '50px',
                                        height: '50px',
                                        borderRadius: '50%',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 4, 83, 0.12)',
                                        },
                                    }}
                                >
                                    <ChevronLeftRoundedIcon sx={{ fontSize: '32px', color: '#000453' }} />
                                </IconButton>

                                <IconButton
                                    onClick={handleNextPage}
                                    disabled={(page + 1) * whiteboardsPerPage >= filtered.length}
                                    sx={{
                                        position: 'absolute',
                                        right: '-10px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        zIndex: 10,
                                        backgroundColor: 'rgba(0, 4, 83, 0.08)',
                                        width: '50px',
                                        height: '50px',
                                        borderRadius: '50%',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 4, 83, 0.12)',
                                        },
                                    }}
                                >
                                    <ChevronRightRoundedIcon sx={{ fontSize: '32px', color: '#000453' }} />
                                </IconButton>
                            </>
                        )}
                    </Grid>
                )}
            </Container>

            {/* Feedback Section */}
            <FeedbackCTA />
        </>
    );
};

export default observer(ArchivedWhiteboards);
